import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { ApiClient } from '../utils/apiClient';
import {
  setSessionExpiredHandler,
  markSessionExpiredNotice,
} from '../utils/sessionExpiry';
import { __resetAvatarCache } from '../utils/avatarImageCache';
import { clearUserScopedCaches } from '../services/dedupedFetch';
import { hasPermission as _hasPermission, hasAnyPermission as _hasAnyPermission } from '../utils/permissions';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  permissions: string[];
  authType: 'LOCAL' | 'SSO';
  /** Owned-avatar URL (`/api/images/:id`). */
  avatarUrl?: string;
  /** Linked-employee fields from the profile endpoint (null when unlinked). */
  employeeId?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  /** From the profile: an Owner linked to a sign-in provider may still hold a password. */
  hasPassword?: boolean;
  ssoLinked?: boolean;
  /**
   * The identity provider this session signed in through, read from the token
   * (absent for a password session). The SSO admin screen warns before the
   * admin switches that provider off.
   */
  idp?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => string | null;
  /** Re-fetch the signed-in user's profile (e.g. after changing their avatar). */
  refreshUser: () => Promise<void>;
  /** Adopt a token minted mid-session, e.g. after changing one's password. */
  replaceToken: (accessToken: string) => void;
}

interface PermissionsHookResult {
  permissions: string[];
  hasPermission: (key: string) => boolean;
  hasAnyPermission: (...keys: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the context for testing purposes
export { AuthContext };
export type { User };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook providing permission-based access checks against the current user's
 * permissions array (populated from the JWT token).
 */
export const usePermissions = (): PermissionsHookResult => {
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];

  const hasPermission = useCallback(
    (key: string) => _hasPermission(permissions, key),
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (...keys: string[]) => _hasAnyPermission(permissions, ...keys),
    [permissions],
  );

  return useMemo(
    () => ({ permissions, hasPermission, hasAnyPermission }),
    [permissions, hasPermission, hasAnyPermission],
  );
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Decode the payload of a JWT without verifying the signature.
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/** Extract the permissions array from a JWT payload (falls back to empty). */
function extractPermissionsFromToken(token: string): string[] {
  const payload = decodeJwtPayload(token);
  if (!payload) return [];
  const perms = (payload as Record<string, unknown>).permissions;
  return Array.isArray(perms) ? perms : [];
}

/** The identity provider a session signed in through, when the token names one. */
function extractIdpFromToken(token: string): string | undefined {
  const idp = decodeJwtPayload(token)?.idp;
  return typeof idp === 'string' ? idp : undefined;
}

/** Ensure a user object always has a permissions array populated. */
function normaliseUser(
  userData: Omit<User, 'permissions'> & { permissions?: string[] },
  token?: string,
): User {
  const permissions =
    userData.permissions ??
    (token ? extractPermissionsFromToken(token) : []);
  return { ...userData, permissions, idp: token ? extractIdpFromToken(token) : undefined };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      fetchUserProfile(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token: string) => {
    try {
      const apiClient = new ApiClient(() => token);
      const userData = await apiClient.get('/api/auth/profile');
      setUser(normaliseUser(userData, token));
    } catch {
      localStorage.removeItem('accessToken');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const apiClient = new ApiClient(() => null);
      const data = await apiClient.post('/api/auth/login', { email, password });

      localStorage.setItem('accessToken', data.accessToken);
      setUser(normaliseUser(data.user, data.accessToken));
      // No `catch`: the error propagates to the sign-in form, which renders it
      // inline. `finally` still runs on the throwing path.
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    setUser(null);
    // Drop cached avatar blob URLs and every cached API response, so the next
    // user never reuses them.
    __resetAvatarCache();
    clearUserScopedCaches();
    // Per-tab state belongs to the session that created it. `App` renders the
    // login screen in place rather than navigating, so the URL survives a
    // logout — and the timesheet review queue keys a snapshot of up to 500
    // employee names off a token in that URL. Without this, the next person to
    // sign in on this tab rehydrates the previous reviewer's roster.
    //
    // The SSO handshake keys, and the page that sign-in returns to, are the
    // deliberate exception, NOT an oversight: they are written on the login
    // screen and read back after the IdP round trip, and a logout can land
    // between the two. A stale token in the shared
    // `localStorage` 401s on the boot profile fetch, which runs while `App` is
    // still showing its spinner — before `AuthCallback` mounts — so a blanket
    // clear would destroy a sign-in that was about to succeed. Do not "tidy"
    // this back into `sessionStorage.clear()`.
    try {
      // Enumerated through `length`/`key(i)`, not `Object.keys`: the latter
      // relies on Storage exposing its entries as own enumerable properties,
      // which browsers do and a Map-backed implementation does not — so it
      // silently clears nothing outside a browser. Collected before removing,
      // because deleting shifts the indices underneath the loop.
      const doomed: string[] = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (key === 'ssoProviderId' || key === 'authReturnPath' || key.startsWith('sso_callback_')) continue;
        doomed.push(key);
      }
      doomed.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* private browsing or storage disabled — nothing was cached to clear */
    }
  }, []);

  // Central handling for an expired/revoked token: any authenticated API call
  // that 401s routes here (via the sessionExpiry bridge). We only act when a
  // token is actually present — a stray 401 while already signed out must not
  // raise a spurious "session expired" notice — then flag the notice and log
  // out, which flips the app back to the login screen (App renders LoginPage
  // whenever `!isAuthenticated`). Registered once; the handler is stable.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (!localStorage.getItem('accessToken')) return;
      markSessionExpiredNotice();
      logout();
    });
    return () => setSessionExpiredHandler(null);
  }, [logout]);

  const getAccessToken = useCallback((): string | null => {
    return localStorage.getItem('accessToken');
  }, []);

  /**
   * Adopt a token the server minted mid-session.
   *
   * A password change revokes every token issued before it — including the one
   * this tab is holding — so the replacement has to be in storage before the
   * next request goes out or the user is signed out of the tab they just used.
   * `getAccessToken` reads storage on every call, so writing it is enough to
   * redirect every in-flight and future request.
   *
   * Validated **before** the write, not after: `setItem` on an absent or
   * malformed token stores the string `"undefined"` over a working one, and
   * because React state still says authenticated the app carries on rendering
   * until the next request 401s into a session teardown. Keeping the old token
   * leaves a working session working.
   *
   * Permissions are re-read from the new token rather than carried over: the
   * token is what the server authorises against, and letting the two disagree
   * is how a UI starts offering what the API refuses.
   */
  const replaceToken = useCallback((accessToken: string) => {
    if (!accessToken || !decodeJwtPayload(accessToken)) return;
    localStorage.setItem('accessToken', accessToken);
    setUser((current) =>
      current
        ? {
            ...current,
            permissions: extractPermissionsFromToken(accessToken),
            idp: extractIdpFromToken(accessToken),
          }
        : current,
    );
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    try {
      const apiClient = new ApiClient(() => token);
      const userData = await apiClient.get('/api/auth/profile');
      setUser(normaliseUser(userData, token));
    } catch {
      // A transient (non-401) failure is non-fatal — keep the current user in
      // place. A 401 means the token is dead: `makeApiCallJson` has already
      // fired the session-expiry handler (logout) before this catch runs, so the
      // fail-closed logout is intentional, not swallowed here.
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    getAccessToken,
    refreshUser,
    replaceToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 